import { MenuItem } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import {
  getFundFamilyRetrieveQueryKey,
  useFundFamilyRetrieveSuspense,
  useFundFamilyUpdate,
} from "gen/endpoints/fund-family/fund-family";
import {
  getFundFamilyConfigRetrieveQueryKey,
  useFundFamilyConfigRetrieveSuspense,
  useFundFamilyConfigUpdate,
} from "gen/endpoints/fund-family-config/fund-family-config";
import { CallForFeesTransactionDateEnum } from "gen/model/callForFeesTransactionDateEnum";
import { EqualizationConfigDateEnum } from "gen/model/equalizationConfigDateEnum";
import { EqualizationInterestMethodologyEnum } from "gen/model/equalizationInterestMethodologyEnum";
import { FundFamilyConfigUpsertDataRequest } from "gen/model/fundFamilyConfigUpsertDataRequest";
import { FundFamilyDataRequest } from "gen/model/fundFamilyDataRequest";
import { GeneralLedgerTypeEnum } from "gen/model/generalLedgerTypeEnum";
import { InterestDayCountEnum } from "gen/model/interestDayCountEnum";
import { InvestorPortalTypeEnum } from "gen/model/investorPortalTypeEnum";
import { NullableRoundingMethodologyEnum } from "gen/model/nullableRoundingMethodologyEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { PrefCalcLimitingFactorEnum } from "gen/model/prefCalcLimitingFactorEnum";
import { RoundingMethodologyEnum } from "gen/model/roundingMethodologyEnum";
import { RoundingPartnerOrderingMethodologyEnum } from "gen/model/roundingPartnerOrderingMethodologyEnum";
import { fundFamilyUpdateBody } from "gen/schemas/fund-family/fund-family";
import { fundFamilyConfigUpdateBody } from "gen/schemas/fund-family-config/fund-family-config";
import React, { useState } from "react";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import { FormBooleanToggleButtonGroup } from "shared/components/button/ToggleButton";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import {
  FormDrawer,
  useFormDrawerInitialFocusRef,
} from "shared/components/form/FormDrawer";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatPercentage } from "shared/utils/formattingUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { isTruthy } from "shared/utils/utils";

export const FundFamilySetupFundTabProfile: React.FC = () => {
  const { fundFamilyId } = useRequiredStringParams();

  const [editingSectionId, setEditingSectionId] = useState<
    | "basic"
    | "advanced"
    | "thirdPartyIntegration"
    | "rounding"
    | "equalizationInterest"
  >();

  return (
    <PageTabBody data-private={true}>
      <ContentBlockGroup>
        <FundFamilyDetailsBasicBlock
          fundFamilyId={fundFamilyId}
          onEdit={() => setEditingSectionId("basic")}
        />
        <FundFamilyDetailsAdvancedBlock
          fundFamilyId={fundFamilyId}
          onEdit={() => setEditingSectionId("advanced")}
        />
        <FundFamilyDetailsThirdPartyBlock
          fundFamilyId={fundFamilyId}
          onEdit={() => setEditingSectionId("thirdPartyIntegration")}
        />
        <FundFamilyDetailsEqualizationInterest
          fundFamilyId={fundFamilyId}
          onEdit={() => setEditingSectionId("equalizationInterest")}
        />
        <FundFamilyDetailsRoundingBlock
          fundFamilyId={fundFamilyId}
          onEdit={() => setEditingSectionId("rounding")}
        />
      </ContentBlockGroup>
      <QueryBoundary>
        {editingSectionId === "basic" && (
          <FundFamilyDetailsBasicForm
            fundFamilyId={fundFamilyId}
            onClose={() => setEditingSectionId(undefined)}
          />
        )}
        {editingSectionId === "advanced" && (
          <FundFamilyDetailsAdvancedForm
            fundFamilyId={fundFamilyId}
            onClose={() => setEditingSectionId(undefined)}
          />
        )}
        {editingSectionId === "equalizationInterest" && (
          <FundFamilyDetailsEqualizationInterestForm
            fundFamilyId={fundFamilyId}
            onClose={() => setEditingSectionId(undefined)}
          />
        )}
        {editingSectionId === "rounding" && (
          <FundFamilyDetailsRoundingForm
            fundFamilyId={fundFamilyId}
            onClose={() => setEditingSectionId(undefined)}
          />
        )}
        {editingSectionId === "thirdPartyIntegration" && (
          <FundFamilyDetailsThirdPartyForm
            fundFamilyId={fundFamilyId}
            onClose={() => setEditingSectionId(undefined)}
          />
        )}
      </QueryBoundary>
    </PageTabBody>
  );
};

const FundFamilyDetailsBasicBlock: React.FC<{
  fundFamilyId: string;
  onEdit?: () => void;
}> = ({ fundFamilyId, onEdit }) => {
  const { t } = useTranslation(["fundFamily", "common", "enums"]);

  const { data: fundFamily } = useFundFamilyRetrieveSuspense(fundFamilyId);

  return (
    <ContentBlock>
      <ContentBlockHeader>
        <Restricted permission={PermissionTypeEnum.config}>
          <ContentBlockActions>
            <ContentBlockMenu>
              <MenuItem onClick={onEdit}>{t("common:edit")}</MenuItem>
            </ContentBlockMenu>
          </ContentBlockActions>
        </Restricted>
      </ContentBlockHeader>
      <ContentBlockBody variant="descriptions">
        <Descriptions>
          <Descriptions.Item label={t("fundFamily:fundFamilyName")}>
            {fundFamily.name}
          </Descriptions.Item>
          <Descriptions.Item label={t("fundFamily:target.raise")}>
            {formatMoney(fundFamily.target_raise)}
          </Descriptions.Item>
          <Descriptions.Item label={t("fundFamily:target.closeDate")}>
            {formatIsoDateToDisplay(fundFamily.target_first_close)}
          </Descriptions.Item>
          <Descriptions.Item label={t("common:notes")}>
            {fundFamily.notes}
          </Descriptions.Item>
        </Descriptions>
      </ContentBlockBody>
    </ContentBlock>
  );
};

const FundFamilyDetailsAdvancedBlock: React.FC<{
  fundFamilyId: string;
  onEdit?: () => void;
}> = ({ fundFamilyId, onEdit }) => {
  const { t } = useTranslation(["fundFamily", "common", "enums"]);

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  return (
    <ContentBlock>
      <ContentBlockHeader>
        <ContentBlockTitle>{t("common:advanced")}</ContentBlockTitle>
        <Restricted permission={PermissionTypeEnum.config}>
          <ContentBlockActions>
            <ContentBlockMenu>
              <MenuItem onClick={onEdit}>{t("common:edit")}</MenuItem>
            </ContentBlockMenu>
          </ContentBlockActions>
        </Restricted>
      </ContentBlockHeader>
      <ContentBlockBody variant="descriptions">
        <Descriptions>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.advanced.prefCalcLimitingFactor",
            )}
          >
            {t(
              `enums:PrefCalcLimitingFactorEnum.${fundFamilyConfig.pref_calc_limiting_factor}`,
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.advanced.callForFeesTransactionDate",
            )}
          >
            {t(
              `enums:CallForFeesTransactionDateEnum.${fundFamilyConfig.call_for_fees_transaction_date}`,
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.advanced.includeGPCashFlowsInGrossIRR",
            )}
          >
            {fundFamilyConfig.include_gp_cash_flows_in_gross_irr
              ? t("common:yes")
              : t("common:no")}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.advanced.includeGPCashFlowsInNetIRR",
            )}
          >
            {fundFamilyConfig.include_gp_cash_flows_in_net_irr
              ? t("common:yes")
              : t("common:no")}
          </Descriptions.Item>
        </Descriptions>
      </ContentBlockBody>
    </ContentBlock>
  );
};

const FundFamilyDetailsEqualizationInterest: React.FC<{
  fundFamilyId: string;
  onEdit?: () => void;
}> = ({ fundFamilyId, onEdit }) => {
  const { t } = useTranslation(["fundFamily", "common", "enums"]);

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  return (
    <ContentBlock>
      <ContentBlockHeader>
        <ContentBlockTitle>
          {t("fundFamily:setup.profile.equalizationInterest.title")}
        </ContentBlockTitle>
        <Restricted permission={PermissionTypeEnum.config}>
          <ContentBlockActions>
            <ContentBlockMenu>
              <MenuItem onClick={onEdit}>{t("common:edit")}</MenuItem>
            </ContentBlockMenu>
          </ContentBlockActions>
        </Restricted>
      </ContentBlockHeader>
      <ContentBlockBody variant="descriptions">
        <Descriptions>
          <Descriptions.Item
            label={t("fundFamily:setup.profile.advanced.catchUpPercentage")}
          >
            {formatPercentage(
              new Decimal(fundFamilyConfig.catch_up_percentage).times(100),
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.advanced.equalizationInterestMethodology",
            )}
          >
            {t(
              `fundFamily:config.equalizationInterestMethodology.${fundFamilyConfig.equalization_interest_methodology}`,
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.advanced.equalizationCatchUpMaterialityThreshold",
            )}
          >
            {formatMoney(
              fundFamilyConfig.equalization_catch_up_materiality_threshold,
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("fundFamily:setup.profile.equalizationInterest.dayCount")}
          >
            {t(
              `fundFamily:config.dayCount.${fundFamilyConfig.equalization_interest_day_count}`,
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.advanced.allowEqualizationInterestOptOut",
            )}
          >
            {fundFamilyConfig.allow_equalization_interest_opt_out
              ? t("common:yes")
              : t("common:no")}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.advanced.equalizationInterestReferenceDate",
            )}
          >
            {t(
              `enums:EqualizationInterestReferenceDateEnum.${fundFamilyConfig.equalization_interest_reference_date}`,
            )}
          </Descriptions.Item>
        </Descriptions>
      </ContentBlockBody>
    </ContentBlock>
  );
};

const FundFamilyDetailsThirdPartyBlock: React.FC<{
  fundFamilyId: string;
  onEdit?: () => void;
}> = ({ fundFamilyId, onEdit }) => {
  const { t } = useTranslation(["fundFamily", "common", "enums"]);

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  return (
    <ContentBlock>
      <ContentBlockHeader>
        <ContentBlockTitle>
          {t("fundFamily:setup.profile.thirdPartyIntegration.title")}
        </ContentBlockTitle>
        <Restricted permission={PermissionTypeEnum.config}>
          <ContentBlockActions>
            <ContentBlockMenu>
              <MenuItem onClick={onEdit}>{t("common:edit")}</MenuItem>
            </ContentBlockMenu>
          </ContentBlockActions>
        </Restricted>
      </ContentBlockHeader>
      <ContentBlockBody variant="descriptions">
        <Descriptions>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.thirdPartyIntegration.generalLedger",
            )}
          >
            {isTruthy(fundFamilyConfig.general_ledger_type)
              ? t(
                  `enums:GeneralLedgerTypeEnum.${fundFamilyConfig.general_ledger_type}`,
                )
              : null}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.thirdPartyIntegration.investorPortal",
            )}
          >
            {isTruthy(fundFamilyConfig.investor_portal_type)
              ? t(
                  `enums:InvestorPortalTypeEnum.${fundFamilyConfig.investor_portal_type}`,
                )
              : null}
          </Descriptions.Item>
        </Descriptions>
      </ContentBlockBody>
    </ContentBlock>
  );
};

const FundFamilyDetailsRoundingBlock: React.FC<{
  fundFamilyId: string;
  onEdit?: () => void;
}> = ({ fundFamilyId, onEdit }) => {
  const { t } = useTranslation(["fundFamily", "common", "enums"]);

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  return (
    <ContentBlock>
      <ContentBlockHeader>
        <ContentBlockTitle>
          {t("fundFamily:setup.profile.rounding.title")}
        </ContentBlockTitle>
        <Restricted permission={PermissionTypeEnum.config}>
          <ContentBlockActions>
            <ContentBlockMenu>
              <MenuItem onClick={onEdit}>{t("common:edit")}</MenuItem>
            </ContentBlockMenu>
          </ContentBlockActions>
        </Restricted>
      </ContentBlockHeader>
      <ContentBlockBody variant="descriptions">
        <Descriptions>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.rounding.roundingPartnerOrderingMethodology",
            )}
          >
            {t(
              `enums:RoundingPartnerOrderingMethodologyEnum.${fundFamilyConfig.rounding_partner_ordering_methodology}`,
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("fundFamily:setup.profile.rounding.roundingMethodology")}
          >
            {t(
              `fundFamily:config.roundingMethodology.${fundFamilyConfig.rounding_methodology}`,
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              "fundFamily:setup.profile.rounding.creditFacilityRoundingMethodologyOverride",
            )}
          >
            {t(
              `enums:RoundingMethodologyEnum.${fundFamilyConfig.credit_facility_rounding_methodology_override}`,
            )}
          </Descriptions.Item>
        </Descriptions>
      </ContentBlockBody>
    </ContentBlock>
  );
};

const FundFamilyDetailsBasicForm: React.FC<{
  fundFamilyId: string;
  onClose: () => void;
}> = ({ fundFamilyId, onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["fundFamily", "common", "enums"]);

  const { data: fundFamily } = useFundFamilyRetrieveSuspense(fundFamilyId);

  const form = useForm<FundFamilyDataRequest>({
    defaultValues: fundFamily as DefaultValues<FundFamilyDataRequest>,
    resolver: zodResolver(fundFamilyUpdateBody),
    mode: "all",
  });

  const { mutateAsync: updateFundFamily } = useFundFamilyUpdate();

  return (
    <FormDrawer
      title={t("fundFamily:edit.title")}
      formProps={{
        form,
        onSubmit: async (data: FundFamilyDataRequest) => {
          const updatedFundFamily = await updateFundFamily({
            fundFamilyId,
            data,
          });
          queryClient.setQueryData(
            getFundFamilyRetrieveQueryKey(fundFamilyId),
            updatedFundFamily,
          );
          return data;
        },
        onSuccess: (data) => form.reset(data),
      }}
      bodyProps={{
        "data-private": true,
      }}
      onCloseComplete={onClose}
    >
      <FundFamilyDetailsBasicFormFields />
    </FormDrawer>
  );
};

const FundFamilyDetailsBasicFormFields: React.FC = () => {
  const { t } = useTranslation(["fundFamily", "common"]);
  const initialFocusRef = useFormDrawerInitialFocusRef();
  return (
    <>
      <FormTextInput
        name="name"
        title={t("fundFamily:fundFamilyName")}
        ref={initialFocusRef}
        autoFocus={true}
      />
      <FormMoneyInput
        name="target_raise"
        title={t("fundFamily:target.raise")}
      />
      <FormDateInput
        name="target_first_close"
        title={t("fundFamily:target.closeDate")}
      />
      <FormTextArea name="notes" title={t("common:notesOptional")} />
    </>
  );
};

const FundFamilyDetailsAdvancedForm: React.FC<{
  fundFamilyId: string;
  onClose: () => void;
}> = ({ fundFamilyId, onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["fundFamily", "common", "enums"]);

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const form = useForm<FundFamilyConfigUpsertDataRequest>({
    defaultValues: fundFamilyConfig,
    resolver: zodResolver(fundFamilyConfigUpdateBody),
    mode: "all",
  });

  const { mutateAsync: updateFundFamilyConfig } = useFundFamilyConfigUpdate();

  return (
    <FormDrawer
      title={t("fundFamily:edit.title")}
      formProps={{
        form,
        onSubmit: async (data: FundFamilyConfigUpsertDataRequest) => {
          const updatedFundFamilyConfig = await updateFundFamilyConfig({
            fundFamilyId,
            data,
          });
          queryClient.setQueryData(
            getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
            updatedFundFamilyConfig,
          );
          return data;
        },
        onSuccess: (data) => form.reset(data),
      }}
      bodyProps={{
        "data-private": true,
      }}
      onCloseComplete={onClose}
    >
      <FundFamilyDetailsAdvancedFormFields />
    </FormDrawer>
  );
};

const FundFamilyDetailsAdvancedFormFields: React.FC = () => {
  const { t } = useTranslation(["fundFamily", "common", "enums"]);
  return (
    <>
      <FormSelect
        name="pref_calc_limiting_factor"
        title={t("fundFamily:setup.profile.advanced.prefCalcLimitingFactor")}
      >
        {Object.values(PrefCalcLimitingFactorEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:PrefCalcLimitingFactorEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <FormBooleanToggleButtonGroup
        name="allow_equalization_interest_opt_out"
        title={t(
          "fundFamily:setup.profile.advanced.allowEqualizationInterestOptOut",
        )}
      />
      <FormSelect
        name="equalization_interest_reference_date"
        title={t(
          "fundFamily:setup.profile.advanced.equalizationInterestReferenceDate",
        )}
      >
        {Object.values(EqualizationConfigDateEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:EqualizationInterestReferenceDateEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <FormSelect
        name="call_for_fees_transaction_date"
        title={t(
          "fundFamily:setup.profile.advanced.callForFeesTransactionDate",
        )}
      >
        {Object.values(CallForFeesTransactionDateEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:CallForFeesTransactionDateEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <FormBooleanToggleButtonGroup
        name="compute_gross_irr_inclusive_of_gp"
        title={t(
          "fundFamily:setup.profile.advanced.includeGPCashFlowsInGrossIRR",
        )}
      />
      <FormBooleanToggleButtonGroup
        name="compute_net_irr_inclusive_of_gp"
        title={t(
          "fundFamily:setup.profile.advanced.includeGPCashFlowsInNetIRR",
        )}
      />
    </>
  );
};

const FundFamilyDetailsEqualizationInterestForm: React.FC<{
  fundFamilyId: string;
  onClose: () => void;
}> = ({ fundFamilyId, onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["fundFamily", "common", "enums"]);

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const form = useForm<FundFamilyConfigUpsertDataRequest>({
    defaultValues: fundFamilyConfig,
    resolver: zodResolver(fundFamilyConfigUpdateBody),
    mode: "all",
  });

  const { mutateAsync: updateFundFamilyConfig } = useFundFamilyConfigUpdate();

  return (
    <FormDrawer
      title={t("fundFamily:edit.title")}
      formProps={{
        form,
        onSubmit: async (data: FundFamilyConfigUpsertDataRequest) => {
          const updatedFundFamilyConfig = await updateFundFamilyConfig({
            fundFamilyId,
            data,
          });
          queryClient.setQueryData(
            getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
            updatedFundFamilyConfig,
          );
          return data;
        },
        onSuccess: (data) => form.reset(data),
      }}
      onCloseComplete={onClose}
    >
      <FundFamilyDetailsEqualizationInterestFields />
    </FormDrawer>
  );
};

const FundFamilyDetailsEqualizationInterestFields: React.FC = () => {
  const { t } = useTranslation(["fundFamily", "common", "enums"]);
  const initialFocusRef = useFormDrawerInitialFocusRef();
  return (
    <>
      <FormNumberInput
        name="catch_up_percentage"
        valueType="percent"
        title={t("fundFamily:setup.profile.advanced.catchUpPercentage")}
        getInputRef={initialFocusRef}
        autoFocus={true}
      />
      <FormNumberInput
        name="equalization_catch_up_materiality_threshold"
        valueType="number"
        title={t(
          "fundFamily:setup.profile.advanced.equalizationCatchUpMaterialityThreshold",
        )}
        getInputRef={initialFocusRef}
        autoFocus={true}
      />
      <FormSelect
        name="equalization_interest_methodology"
        title={t(
          "fundFamily:setup.profile.advanced.equalizationInterestMethodology",
        )}
      >
        {Object.values(EqualizationInterestMethodologyEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:EqualizationInterestMethodologyEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <FormSelect
        name="equalization_interest_day_count"
        title={t("fundFamily:setup.profile.equalizationInterest.dayCount")}
      >
        {Object.values(InterestDayCountEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:InterestDayCountEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <FormBooleanToggleButtonGroup
        name="allow_equalization_interest_opt_out"
        title={t(
          "fundFamily:setup.profile.advanced.allowEqualizationInterestOptOut",
        )}
      />
      <FormSelect
        name="equalization_interest_reference_date"
        title={t(
          "fundFamily:setup.profile.advanced.equalizationInterestReferenceDate",
        )}
      >
        {Object.values(EqualizationConfigDateEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:EqualizationInterestReferenceDateEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
    </>
  );
};

const FundFamilyDetailsRoundingForm: React.FC<{
  fundFamilyId: string;
  onClose: () => void;
}> = ({ fundFamilyId, onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["fundFamily", "common", "enums"]);

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const form = useForm<FundFamilyConfigUpsertDataRequest>({
    defaultValues: fundFamilyConfig,
    resolver: zodResolver(fundFamilyConfigUpdateBody),
    mode: "all",
  });

  const { mutateAsync: updateFundFamilyConfig } = useFundFamilyConfigUpdate();

  return (
    <FormDrawer
      title={t("fundFamily:edit.title")}
      formProps={{
        form,
        onSubmit: async (data: FundFamilyConfigUpsertDataRequest) => {
          const updatedFundFamilyConfig = await updateFundFamilyConfig({
            fundFamilyId,
            data,
          });
          queryClient.setQueryData(
            getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
            updatedFundFamilyConfig,
          );
          return data;
        },
        onSuccess: (data) => form.reset(data),
      }}
      bodyProps={{
        "data-private": true,
      }}
      onCloseComplete={onClose}
    >
      <FundFamilyDetailsRoundingFormFields />
    </FormDrawer>
  );
};

const FundFamilyDetailsRoundingFormFields: React.FC = () => {
  const { t } = useTranslation(["enums", "fundFamily"]);
  return (
    <>
      <FormSelect
        name="rounding_partner_ordering_methodology"
        title={t(
          "fundFamily:setup.profile.rounding.roundingPartnerOrderingMethodology",
        )}
      >
        {Object.values(RoundingPartnerOrderingMethodologyEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:RoundingPartnerOrderingMethodologyEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <FormSelect
        name="rounding_methodology"
        title={t("fundFamily:setup.profile.rounding.roundingMethodology")}
      >
        {Object.values(RoundingMethodologyEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:RoundingMethodologyEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <FormSelect
        name="credit_facility_rounding_methodology_override"
        title={t(
          "fundFamily:setup.profile.rounding.creditFacilityRoundingMethodologyOverride",
        )}
      >
        {Object.values(NullableRoundingMethodologyEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:RoundingMethodologyEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
    </>
  );
};

const FundFamilyDetailsThirdPartyForm: React.FC<{
  fundFamilyId: string;
  onClose: () => void;
}> = ({ fundFamilyId, onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["fundFamily", "common", "enums"]);

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const form = useForm<FundFamilyConfigUpsertDataRequest>({
    defaultValues: fundFamilyConfig,
    resolver: zodResolver(fundFamilyConfigUpdateBody),
    mode: "all",
  });

  const { mutateAsync: updateFundFamilyConfig } = useFundFamilyConfigUpdate();

  return (
    <FormDrawer
      title={t("fundFamily:edit.title")}
      formProps={{
        form,
        onSubmit: async (data: FundFamilyConfigUpsertDataRequest) => {
          const updatedFundFamilyConfig = await updateFundFamilyConfig({
            fundFamilyId,
            data,
          });
          queryClient.setQueryData(
            getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
            updatedFundFamilyConfig,
          );
          return data;
        },
        onSuccess: (data) => form.reset(data),
      }}
      bodyProps={{
        "data-private": true,
      }}
      onCloseComplete={onClose}
    >
      <FundFamilyDetailsThirdPartyFormFields />
    </FormDrawer>
  );
};

const FundFamilyDetailsThirdPartyFormFields: React.FC = () => {
  const { t } = useTranslation(["enums", "fundFamily"]);
  return (
    <>
      <FormSelect
        name="general_ledger_type"
        title={t("fundFamily:config.generalLedger.title")}
      >
        {Object.values(GeneralLedgerTypeEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:GeneralLedgerTypeEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <FormSelect
        name="investor_portal_type"
        title={t("fundFamily:config.investorPortal.title")}
      >
        {Object.values(InvestorPortalTypeEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:InvestorPortalTypeEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
    </>
  );
};
